import { useCallback, useEffect, useRef } from 'react';
import { supabase } from './client';
import type { CartItem, PromoCode } from '@/contexts/CartContext';
import type { PromotionType } from '@/lib/promotions';

/* ── Checkout data shape (form state preserved for abandoned carts) ── */
export interface CheckoutData {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  street?: string;
  zip?: string;
  city?: string;
  country?: number;
  shippingMethod?: 'standard';
  checkoutStep?: number;
  promoInput?: string;
  test_payment?: boolean;
  test_payment_provider?: string | null;
  test_timestamp?: string;
}

const SESSION_KEY = 'h2h-cart-session-id';
const STORAGE_ITEMS_KEY = 'h2h-cart-items';
const STORAGE_PROMO_KEY = 'h2h-cart-promo';
const STORAGE_CHECKOUT_KEY = 'h2h-cart-checkout';

/* ── Session ID (persistent, generated once) ── */
export function getOrCreateSessionId(): string {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

/* ── Load cart from Supabase, with localStorage fallback ── */
export async function loadCartFromSupabase(): Promise<{
  items: CartItem[];
  promoCode: PromoCode | null;
  checkoutData: CheckoutData | null;
}> {
  const sessionId = getOrCreateSessionId();

  try {
    const { data, error } = await supabase.rpc('get_cart', { p_session_id: sessionId });

    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : (data as any);

    if (row) {
      const data = row as { items: unknown; promo_code: unknown; checkout_data: unknown };
      // Validate items is an array
      const items = Array.isArray(data.items) ? (data.items as unknown as CartItem[]) : [];
      let promoCode: PromoCode | null = null;
      if (
        data.promo_code &&
        typeof data.promo_code === 'object' &&
        'code' in (data.promo_code as any) &&
        'stripePromotionCodeId' in (data.promo_code as any)
      ) {
        promoCode = data.promo_code as unknown as PromoCode;
        // Backward compat: old stored codes won't have promotionType
        if (!('promotionType' in (data.promo_code as any))) {
          promoCode = { ...promoCode, promotionType: 'first_purchase_individual' as PromotionType };
        }
      }
      let checkoutData: CheckoutData | null = null;
      if (data.checkout_data && typeof data.checkout_data === 'object') {
        checkoutData = data.checkout_data as CheckoutData;
      }
      return { items, promoCode, checkoutData };
    }
  } catch (e) {
    console.warn('Failed to load cart from Supabase, using localStorage fallback:', e);
  }

  // Fallback to localStorage
  return loadCartFromLocalStorage();
}

/* ── Load cart from localStorage ── */
function loadCartFromLocalStorage(): { items: CartItem[]; promoCode: PromoCode | null; checkoutData: CheckoutData | null } {
  let items: CartItem[] = [];
  let promoCode: PromoCode | null = null;
  let checkoutData: CheckoutData | null = null;
  try {
    const raw = localStorage.getItem(STORAGE_ITEMS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) items = parsed;
    }
    const promo = localStorage.getItem(STORAGE_PROMO_KEY);
    if (promo) {
      const parsed = JSON.parse(promo);
      if (parsed && typeof parsed.code === 'string' && typeof parsed.stripePromotionCodeId === 'string') {
        promoCode = parsed;
      }
    }
    const chk = localStorage.getItem(STORAGE_CHECKOUT_KEY);
    if (chk) {
      const parsed = JSON.parse(chk);
      if (parsed && typeof parsed === 'object') checkoutData = parsed;
    }
  } catch {}
  return { items, promoCode, checkoutData };
}

/* ── Debounced upsert to Supabase ── */
let pendingUpsert: ReturnType<typeof setTimeout> | null = null;

export function scheduleCartUpsert(items: CartItem[], promoCode: PromoCode | null, checkoutData?: CheckoutData | null): void {
  // Always persist to localStorage as immediate fallback
  try {
    localStorage.setItem(STORAGE_ITEMS_KEY, JSON.stringify(items));
    if (promoCode) {
      localStorage.setItem(STORAGE_PROMO_KEY, JSON.stringify(promoCode));
    } else {
      localStorage.removeItem(STORAGE_PROMO_KEY);
    }
    if (checkoutData !== undefined) {
      if (checkoutData) {
        localStorage.setItem(STORAGE_CHECKOUT_KEY, JSON.stringify(checkoutData));
      } else {
        localStorage.removeItem(STORAGE_CHECKOUT_KEY);
      }
    }
  } catch (error) {
    console.warn('localStorage non blocking error:', error);
  }

  // Debounce Supabase upsert
  if (pendingUpsert) clearTimeout(pendingUpsert);
  pendingUpsert = setTimeout(async () => {
    pendingUpsert = null;
    const sessionId = getOrCreateSessionId();
    try {
      const { error } = await supabase.rpc('upsert_cart', {
        p_session_id: sessionId,
        p_items: items as any,
        p_promo_code: (promoCode as any) ?? null,
        p_checkout_data: (checkoutData ?? null) as any,
        p_update_checkout: checkoutData !== undefined,
      });
      if (error) console.warn('Cart upsert error:', error);
    } catch (e) {
      console.warn('Cart upsert failed (offline?):', e);
    }
  }, 800);
}

/* ── Hook for initial cart hydration ── */
export function useCartHydration(
  setItems: (items: CartItem[]) => void,
  setPromoCode: (code: PromoCode | null) => void,
  setCheckoutData: (data: CheckoutData | null) => void,
  hydrated: boolean,
  setHydrated: (v: boolean) => void
): void {
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current || hydrated) return;
    fetched.current = true;

    loadCartFromSupabase().then((result) => {
      setItems(result.items);
      setPromoCode(result.promoCode);
      setCheckoutData(result.checkoutData);
      setHydrated(true);
    });
  }, [hydrated, setItems, setPromoCode, setCheckoutData, setHydrated]);
}